import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import EditingReturnBanner from '../projects/EditingReturnBanner'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'

export default function WpsWelderDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const previewReturnTo = searchParams.get('returnTo')
  const { getProject } = useProjects()
  const { getSection, updateSection } = useWpsSections()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [welderName, setWelderName] = useState('')
  const [welderId, setWelderId] = useState('')
  const [operator, setOperator] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('welder')
    if (parsed) {
      try {
        setWelderName(parsed.welderName || '')
        setWelderId(parsed.welderId || '')
        setOperator(parsed.operator || '')
      } catch (error) {
        // ignore
      }
    }
  }, [pipeId, getSection])

  const handleSave = async () => {
    if (!pipeId || saving) return
    setSaving(true)
    try {
      await updateSection('welder', { welderName, welderId, operator })
      toast.success('Welder details saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save welder details')
    } finally {
      setSaving(false)
    }
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Welder Details not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={previewReturnTo} contextLabel="Welder Details" />
      <PageHeader
        backTo={{ onClick: () => navigate(-1), label: 'Back' }}
        title={
          <>
            <span>Welder Details</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button loading={saving} disabled={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-medium text-gray-500">Welder Name</label>
          <input
            className="input-primary mt-1"
            value={welderName}
            onChange={(event) => setWelderName(event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Welder ID</label>
          <input
            className="input-primary mt-1"
            value={welderId}
            onChange={(event) => setWelderId(event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">Operator</label>
          <input
            className="input-primary mt-1"
            value={operator}
            onChange={(event) => setOperator(event.target.value)}
          />
        </div>
      </div>
    </div>
  )
}
